import * as yup from 'yup'
import {
  AccountFieldConfig,
  AccountFieldKey,
  AccountFieldsConfig,
} from '@/types/utilityProgram'
import { UtilityAccount } from '@/types'

/**
 * Minimal shape of a utility program needed to resolve its account-fields
 * config. Accepts either `UtilityProgram` variant in the codebase.
 */
type ProgramAccountFieldsSource = {
  account_fields_config?: AccountFieldsConfig | null
  view_config_json?: {
    accountNumberEntry?: {
      accountNumberLabel?: string | null
      accountNumberLength?: number | null
      accountNumberEntryRequired?: boolean | null
    } | null
  } | null
}

export const ACCOUNT_FIELD_KEYS: AccountFieldKey[] = [
  'account_number',
  'contract_number',
  'meter_number',
]

export const DEFAULT_ACCOUNT_FIELD_LABELS: Record<AccountFieldKey, string> = {
  account_number: 'Account Number',
  contract_number: 'Contract Number',
  meter_number: 'Meter Number',
}

/**
 * Resolves which account fields the program collects.
 * - A present `account_fields_config` (including an empty array) is the source
 *   of truth — an empty array means "no account fields" (the step is skipped).
 * - A missing config falls back to a single account-number field built from the
 *   legacy CMS view config, preserving behaviour for unmigrated programs.
 */
export function resolveAccountFieldsConfig(
  program: ProgramAccountFieldsSource | null | undefined,
): AccountFieldsConfig {
  const config = program?.account_fields_config
  if (config) {
    return config
  }

  const legacy = program?.view_config_json?.accountNumberEntry
  return [
    {
      field: 'account_number',
      required: legacy?.accountNumberEntryRequired ?? true,
      label:
        legacy?.accountNumberLabel ??
        DEFAULT_ACCOUNT_FIELD_LABELS.account_number,
      validationLength: legacy?.accountNumberLength ?? null,
    },
  ]
}

/**
 * Initial form values for the configured fields. Each field prefers a saved
 * utility-account value, then falls back to a value extracted from a bill (so a
 * bill uploaded on an earlier step pre-populates the fields rather than only
 * showing an "extracted from bill" hint).
 */
export function buildInitialValues(
  config: AccountFieldsConfig,
  savedAccount: Partial<UtilityAccount> | null | undefined,
  inProgressAccountNumber?: string | null,
  extraction?: Partial<Record<AccountFieldKey, string>> | null,
): Record<AccountFieldKey, string> {
  const values: Record<AccountFieldKey, string> = {
    account_number: '',
    contract_number: '',
    meter_number: '',
  }

  for (const { field } of config) {
    const saved = savedAccount?.[field]
    if (typeof saved === 'string' && saved.trim().length > 0) {
      values[field] = saved
    } else if (isValidExtractedValue(extraction?.[field])) {
      values[field] = extraction?.[field] as string
    }
  }

  // Prefer an account number captured earlier in this enrollment flow.
  if (
    config.some((f) => f.field === 'account_number') &&
    inProgressAccountNumber
  ) {
    values.account_number = inProgressAccountNumber
  }

  return values
}

/** Length-validation message for a field, or null when no length is configured. */
export function getLengthHint(field: AccountFieldConfig): string | null {
  if (field.validationLength == null) {
    return null
  }
  return `Your ${field.label} should be ${field.validationLength} digits long`
}

/** Length of a value counting only alphanumeric characters (ignores spaces, dashes, etc.). */
export function getAlphanumericLength(value: string): number {
  return value.replace(/[^a-zA-Z0-9]/g, '').length
}

/**
 * Yup schema for the configured account fields:
 * - required fields must be non-empty, and
 * - any field with a `validationLength` must match that length once a value is
 *   entered (optional fields only validate length when the user fills them in).
 */
export function buildAccountFieldsValidationSchema(
  config: AccountFieldsConfig,
) {
  const shape: Record<string, yup.StringSchema> = {}
  for (const field of config) {
    let schema = yup.string()
    if (field.required) {
      schema = schema.required(`${field.label} is required`)
    }
    if (field.validationLength != null) {
      const length = field.validationLength
      schema = schema.test(
        'length',
        getLengthHint(field) ?? '',
        (value) => !value || getAlphanumericLength(value) === length,
      )
    }
    shape[field.field] = schema
  }
  return yup.object(shape)
}

/** Helper text shown beneath a field that was autofilled from a bill upload. */
export function extractedHelperText(value: string): string {
  return `Extracted from bill: ${value}`
}

/** Bill-upload success message, pluralised by how many numbers were extracted. */
export function buildUploadSuccessMessage(extractedCount: number): string {
  const pronoun = extractedCount === 1 ? 'it' : 'them'
  return `Bill successfully uploaded and we extracted the number(s) we could and filled ${pronoun} in below.`
}

/**
 * Guards against empty/placeholder extraction values. The backend may return the
 * literal string "null" when it cannot read a number from the bill.
 */
export function isValidExtractedValue(
  value: string | undefined | null,
): value is string {
  if (!value) {
    return false
  }
  return value.toLowerCase() !== 'null'
}

/**
 * The step is complete when every required field has a value. Optional-only (or
 * empty) configs are always complete, so the user can proceed without entering
 * anything.
 */
export function computeAccountFieldsCompleted(
  config: AccountFieldsConfig,
  savedAccount: Partial<UtilityAccount> | null | undefined,
): boolean {
  const requiredFields = config.filter((f) => f.required)
  if (requiredFields.length === 0) {
    return true
  }
  return requiredFields.every((f) => {
    const value = savedAccount?.[f.field]
    return typeof value === 'string' && value.trim().length > 0
  })
}

/** PATCH body containing only the fields the program is configured to collect. */
export function buildPatchPayload(
  config: AccountFieldsConfig,
  values: Partial<Record<AccountFieldKey, string>>,
): Partial<UtilityAccount> {
  const payload: Partial<UtilityAccount> = {}
  for (const { field } of config) {
    payload[field] = values[field] ?? ''
  }
  return payload
}

export type ReviewAccountDetail = {
  field: AccountFieldKey
  label: string
  value: string
}

/**
 * Account-detail rows for the enrollment review page: one per configured field
 * that actually has a value. The account number is taken from the in-progress
 * enrollment (the value just entered in the flow), falling back to the saved
 * utility account; contract/meter come from the saved utility account. Labels
 * come from the program's resolved config, so they match the (possibly
 * CMS-overridden) labels shown on the account-details step.
 */
export function buildReviewAccountDetails(
  config: AccountFieldsConfig,
  inProgressAccountNumber: string | null | undefined,
  savedAccount: Partial<UtilityAccount> | null | undefined,
): ReviewAccountDetail[] {
  return config
    .map((field) => {
      const value =
        field.field === 'account_number'
          ? inProgressAccountNumber ?? savedAccount?.account_number ?? null
          : savedAccount?.[field.field] ?? null
      return { field: field.field, label: field.label, value }
    })
    .filter((detail): detail is ReviewAccountDetail =>
      isValidExtractedValue(detail.value),
    )
}
